import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { LOGO_URL_LIGHT } from "@/data/projectImages";

const Footer = () => {
  const { t } = useT();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-zinc-950 text-zinc-300 border-t border-zinc-800" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12">
          {/* Brand */}
          <div>
            <div className="mb-6">
              <img src={LOGO_URL_LIGHT} alt="SF-Maler Malergeschäft Sammy Felber" className="h-16 sm:h-20 w-auto" />
            </div>
            <p className="text-sm leading-relaxed text-zinc-400">
              {t.common.tagline}. Eidg. dipl. Maler. Seit 2018 für Sie in Basel.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href="https://www.instagram.com/sammyfelber/" target="_blank" rel="noreferrer" className="p-2 border border-zinc-800 hover:border-red-600 hover:text-red-500 transition-colors" data-testid="footer-instagram">
                <Instagram size={18} />
              </a>
              <a href="https://www.facebook.com/people/Malergesch%C3%A4ft-Sammy-Felber/100063633123963/" target="_blank" rel="noreferrer" className="p-2 border border-zinc-800 hover:border-red-600 hover:text-red-500 transition-colors" data-testid="footer-facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-display font-bold text-white text-sm uppercase tracking-widest mb-5">{t.footer.links_title}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="hover:text-red-500 transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/dienstleistungen" className="hover:text-red-500 transition-colors">{t.nav.services}</Link></li>
              <li><Link to="/referenzen" className="hover:text-red-500 transition-colors">{t.nav.gallery}</Link></li>
              <li><Link to="/team" className="hover:text-red-500 transition-colors">{t.nav.team}</Link></li>
              <li><Link to="/ueber-uns" className="hover:text-red-500 transition-colors">{t.nav.about}</Link></li>
              <li><Link to="/kontakt" className="hover:text-red-500 transition-colors">{t.nav.contact}</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-bold text-white text-sm uppercase tracking-widest mb-5">{t.footer.services_title}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/dienstleistungen/malerarbeiten" className="hover:text-red-500 transition-colors">{t.services.items.malerarbeiten.title}</Link></li>
              <li><Link to="/dienstleistungen/tapezier" className="hover:text-red-500 transition-colors">{t.services.items.tapezier.title}</Link></li>
              <li><Link to="/dienstleistungen/fassaden" className="hover:text-red-500 transition-colors">{t.services.items.fassaden.title}</Link></li>
              <li><Link to="/dienstleistungen/daemmung" className="hover:text-red-500 transition-colors">{t.services.items.daemmung.title}</Link></li>
              <li><Link to="/dienstleistungen/holzmalerei" className="hover:text-red-500 transition-colors">{t.services.items.holzmalerei.title}</Link></li>
              <li><Link to="/dienstleistungen/trockenbau" className="hover:text-red-500 transition-colors">{t.services.items.trockenbau.title}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-bold text-white text-sm uppercase tracking-widest mb-5">{t.footer.contact_title}</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3"><MapPin size={16} className="text-red-500 mt-0.5 flex-shrink-0" /><span>{t.common.address}</span></li>
              <li className="flex gap-3"><Phone size={16} className="text-red-500 mt-0.5 flex-shrink-0" /><a href="tel:+41796070148" className="hover:text-red-500">{t.common.phone}</a></li>
              <li className="flex gap-3"><Mail size={16} className="text-red-500 mt-0.5 flex-shrink-0" /><a href="mailto:info@sf-maler.ch" className="hover:text-red-500">{t.common.email}</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p>© {year} {t.common.company}. {t.footer.rights}</p>
          <p>Basel · Schweiz</p>
        </div>
      </div>

      {/* Agency credit - glowing, attention-grabbing */}
      <div className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-center">
          <a
            href="https://alpenpro.agency/"
            target="_blank"
            rel="noreferrer"
            className="agency-credit inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-red-600 via-red-500 to-red-600 rounded-full transition-all hover:scale-105"
            data-testid="agency-credit"
          >
            <span className="agency-sparkle">✦</span>
            Erstellt von <span className="underline decoration-white/70 underline-offset-2">Alpen Pro Agency</span>
            <span className="agency-sparkle">✦</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
